import ky, { HTTPError } from 'ky';
import { FlikHttpAuth, FlikHttpAuthOptions } from '../auth';
import {
  ACCESS_TOKEN_EXPIRED_DETAIL,
  DEFAULT_HTTP_TIMEOUT_MS,
  INVALID_ACCESS_TOKEN_DETAIL,
} from '../consts';
import {
  BAD_GATEWAY_STATUS_CODE,
  GATEWAY_TIMEOUT_STATUS_CODE,
  INTERNAL_SERVER_ERROR_STATUS_CODE,
  SERVICE_UNAVAILABLE_STATUS_CODE,
  TOO_MANY_REQUESTS_STATUS_CODE,
  UNAUTHORIZED_STATUS_CODE,
} from '../http';
import {
  calculateExponentialBackoffDelay,
  DEFAULT_MAX_DELAY_MS,
  DEFAULT_MAX_RETRIES,
  ExponentialBackoffOptions,
} from '../retries';

interface KyClientOptions {
  uri: () => string;
  auth: FlikHttpAuthOptions;
  backoff?: ExponentialBackoffOptions;
  timeout?: number; // Timeout in milliseconds
}

export const createKyClient = (options: KyClientOptions) => {
  const auth = FlikHttpAuth(options.auth);

  const kyInstance = ky.extend({
    prefixUrl: options.uri(),
    timeout: options.timeout ?? DEFAULT_HTTP_TIMEOUT_MS, // Default 30 seconds timeout, configurable
    retry: {
      // we should only retry idempotent methods
      methods: [
        'get',
        'post',
        'put',
        'patch',
        'head',
        'delete',
        'options',
        'trace',
      ],
      statusCodes: [
        UNAUTHORIZED_STATUS_CODE,
        TOO_MANY_REQUESTS_STATUS_CODE,
        BAD_GATEWAY_STATUS_CODE,
        INTERNAL_SERVER_ERROR_STATUS_CODE,
        SERVICE_UNAVAILABLE_STATUS_CODE,
        GATEWAY_TIMEOUT_STATUS_CODE,
      ],
      limit: options.backoff?.maxRetries ?? DEFAULT_MAX_RETRIES,
      backoffLimit: options.backoff?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS,
      maxRetryAfter: options.backoff?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS,
      delay: (attemptCount) => {
        return calculateExponentialBackoffDelay(
          attemptCount,
          options.backoff?.factor,
          options.backoff?.initialDelayMs,
        );
      },
    },
    hooks: {
      beforeRetry: [
        async ({ request, error }) => {
          if (error instanceof HTTPError) {
            const status = error.response?.status;
            const method = request.method.toUpperCase();

            // Safely try to parse the response body
            let body;
            try {
              body = await error.response.clone().json();
            } catch (parseError) {
              // If JSON parsing fails, try to get text instead
              try {
                const text = await error.response.clone().text();
                body = { detail: text || 'Unknown error' };
              } catch (textError) {
                body = { detail: 'Unable to parse error response' };
              }
            }

            if (
              status === UNAUTHORIZED_STATUS_CODE &&
              body.detail === ACCESS_TOKEN_EXPIRED_DETAIL
            ) {
              await auth.refreshAccessToken(options.backoff);
              request.headers.set(
                auth.headerName,
                `Bearer ${auth.getAccessToken()}`,
              );
            }

            if (
              status === UNAUTHORIZED_STATUS_CODE &&
              body.detail === INVALID_ACCESS_TOKEN_DETAIL
            ) {
              await auth.clearSession();
              return ky.stop;
            }

            if (method === 'POST') {
              // For POST requests, stop retrying and throw the error
              throw error;
            }
          }
          return undefined;
        },
      ],
      beforeRequest: [
        async (request) => {
          const path = new URL(request.url).pathname;

          if (auth.pathSkipped(path)) {
            return;
          }

          const token = auth.getAccessToken();
          if (token) {
            request.headers.set(auth.headerName, `Bearer ${token}`);
          }
        },
      ],
    },
  });

  return kyInstance;
};
